import { motion } from "framer-motion";
import { Target, Eye, Heart, Sparkles, Award } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { BRAND } from "@/lib/data";

const VALUES = [
  { icon: Sparkles, title: "Trust", desc: "We keep every commitment made to our customers." },
  { icon: Eye, title: "Transparency", desc: "Clear pricing, honest advice — no hidden charges." },
  { icon: Target, title: "Customer Success", desc: "Your profitable operations are our KPI." },
  { icon: Award, title: "Professionalism", desc: "Trained team, structured processes, real accountability." },
  { icon: Heart, title: "Long-term Relationships", desc: "We stay with you well beyond delivery." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[900px] h-[500px] radial-glow-red pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-red-500 mb-4 font-mono-brand justify-center">
            <span className="w-8 h-px bg-current" /> About Us
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.05] text-white">
            Making Commercial Vehicle <span className="text-gradient-red">Ownership</span> Simple
          </h1>
          <p className="mt-6 text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
            {BRAND.name} was established with a vision to simplify commercial
            vehicle ownership for drivers, first-time entrepreneurs and fleet
            operators — all under one roof.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-3xl overflow-hidden aspect-[4/3]">
            <img
              src="https://images.pexels.com/photos/29566884/pexels-photo-29566884.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Premium car interior"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <SectionHeading eyebrow="Our Story" title="Built for the Yellow-Plate Entrepreneur" />
            <p className="text-slate-300 text-lg leading-relaxed">
              We understand the challenges faced by drivers and first-time entrepreneurs
              when starting a commercial taxi business — vehicle selection, financing,
              permits, insurance, and getting attached to a reliable operator.
            </p>
            <p className="text-slate-400 leading-relaxed mt-4">
              At Be Easy Cars, we bring all of that together with a professional team,
              trusted vehicle partners and streamlined documentation — so you can go from
              enquiry to your first commercial ride in the shortest possible time.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            { label: "Mission", title: "Empower Taxi Entrepreneurs", desc: "Provide reliable commercial vehicles and comprehensive support to help operators build a sustainable and profitable transport business.", icon: Target },
            { label: "Vision", title: "Maharashtra's Most Trusted T-Permit Company", desc: "Set the benchmark for transparency, service excellence and customer success in the commercial vehicle industry.", icon: Eye },
          ].map(({ label, title, desc, icon: Icon }) => (
            <div key={label} className="glass rounded-3xl p-8 md:p-10">
              <Icon className="w-8 h-8 text-red-500 mb-5" />
              <div className="text-xs uppercase tracking-[0.28em] text-slate-400 font-mono-brand">{label}</div>
              <h3 className="font-display text-3xl font-bold text-white mt-3">{title}</h3>
              <p className="mt-4 text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Core values */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="Core Values" title="What We Stand For" />
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass glass-hover rounded-2xl p-6"
                data-testid={`value-card-${i}`}
              >
                <v.icon className="w-6 h-6 text-red-500 mb-4" />
                <div className="font-display font-bold text-white text-lg">{v.title}</div>
                <div className="text-sm text-slate-400 mt-2 leading-relaxed">{v.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
